'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Device, Lang } from '@/lib/types';

// Per-bucket energy usage table for the Device Monitor page. Shown for energy /
// 3-phase power-meter devices. Reads the cumulative kWh meter via
// /api/devices/[mac]/energy-usage and shows Start / End / Usage per bucket,
// where Usage = End − Start (the subtraction the meter reading implies).
// The period (hourly / daily), range and meter field are user-selectable.

interface UsageRow {
  label: string;
  bucketKey?: string | null;
  start: number | null;
  end: number | null;
  usage: number | null;
  avgKw?: number | null;
  expectedKwh?: number | null;
  anomaly?: boolean;
  // TOU split (kWh). For an hour bucket one side holds the whole usage; for a
  // day/month bucket both are summed from that bucket's hourly deltas, and
  // `splitTotal` is what those deltas add up to (see the energy-usage route).
  onpeak?: number | null;
  offpeak?: number | null;
  splitTotal?: number | null;
}
interface UsageResponse {
  rows: UsageRow[];
  unit?: string;
  field?: string;
  meta?: {
    reason?: string; lastSeen?: string | null; powerField?: string | null; anomalyCount?: number;
    peakConfigured?: boolean;
    tou?: { onPeak: string; offPeak: string; onPeakTh: string; offPeakTh: string };
  };
}

// Cumulative kWh fields on a device — detected by unit (kWh), not device type,
// so any energy / 3-phase meter qualifies regardless of its type string.
// e.g. "IMP Energy" / "EXP Energy" (unit kWh) → keys imp_energy / exp_energy.
export function deviceEnergyFields(
  device: Device | null | undefined
): { key: string; label: string }[] {
  if (!device) return [];
  const out: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  const add = (k: string) => {
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ key: k, label: device.fieldNames?.[k] || k });
  };
  for (const [k, u] of Object.entries(device.units || {})) {
    if (/kw\.?h/i.test(u || '')) add(k);
  }
  if ((device.units?.kwh !== undefined || device.data?.kwh !== undefined) && !seen.has('kwh')) add('kwh');
  return out;
}

// Best default field: import/consumption energy first, else 'kwh', else first.
function defaultEnergyKey(fields: { key: string; label: string }[]): string {
  const imp = fields.find((f) => /imp|import|ใช้|consum/i.test(`${f.key} ${f.label}`));
  if (imp) return imp.key;
  const kwh = fields.find((f) => f.key === 'kwh');
  return kwh?.key || fields[0]?.key || 'kwh';
}

// Range options offered per bucket (kept small so tables stay readable).
const RANGE_OPTIONS: Record<'hour' | 'day' | 'month', { value: string; th: string; en: string }[]> = {
  hour: [
    { value: '1d', th: '24 ชม.', en: '24 h' },
    { value: '2d', th: '2 วัน', en: '2 d' },
    { value: '7d', th: '7 วัน', en: '7 d' },
  ],
  day: [
    { value: '7d', th: '7 วัน', en: '7 d' },
    { value: '30d', th: '30 วัน', en: '30 d' },
    { value: '90d', th: '90 วัน', en: '90 d' },
  ],
  month: [
    { value: '180d', th: '6 เดือน', en: '6 mo' },
    { value: '365d', th: '12 เดือน', en: '12 mo' },
  ],
};

const GOLD = '#F59E0B';
// TOU period colours: warm for on-peak (the expensive window), cool for
// off-peak. Kept clear of the sky-blue average line and the red anomaly fill.
const TOU_ON = '#F97316';
const TOU_OFF = '#6366F1';

// Today's date (Asia/Bangkok) as YYYY-MM-DD — used as the date-picker max.
const bkkToday = (): string => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);

const num = (v: number | null | undefined, digits = 2): string =>
  v == null
    ? '—'
    : v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Compact large numbers for KPI cards (1.2k / 3.4M) while keeping 2-dp precision
// for small values.
const compact = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })}M`;
  if (a >= 1_000) return `${(v / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 })}k`;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export default function MonitorEnergyTable({
  device,
  lang,
  enabled,
}: {
  device: Device;
  lang: Lang;
  enabled: boolean;
}) {
  const th = lang === 'th';

  const energyFields = useMemo(() => deviceEnergyFields(device), [device]);
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const fieldKey =
    pickedKey && energyFields.some((f) => f.key === pickedKey)
      ? pickedKey
      : defaultEnergyKey(energyFields);
  const fieldLabel = energyFields.find((f) => f.key === fieldKey)?.label || fieldKey;

  const [bucket, setBucket] = useState<'hour' | 'day' | 'month'>('day');
  const [range, setRange] = useState('7d');
  const [date, setDate] = useState('');       // single-day drill-down (ISO); '' = range mode
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const displayBucket: 'hour' | 'day' | 'month' = date ? 'hour' : bucket;
  const [snap, setSnap] = useState<{ key: string; data: UsageResponse | null; error: string | null }>(
    { key: '', data: null, error: null }
  );

  const sn = device.sn;
  const activeKey = enabled && sn ? (date ? `${sn}|${fieldKey}|date:${date}` : `${sn}|${fieldKey}|${range}|${bucket}`) : '';

  useEffect(() => {
    if (!activeKey || !sn) return;
    const ctrl = new AbortController();
    const url = date
      ? `/api/devices/${encodeURIComponent(sn)}/energy-usage?bucket=hour&date=${date}&key=${encodeURIComponent(fieldKey)}`
      : `/api/devices/${encodeURIComponent(sn)}/energy-usage?range=${range}&bucket=${bucket}&key=${encodeURIComponent(fieldKey)}`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d: UsageResponse) => setSnap({ key: activeKey, data: d, error: null }))
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setSnap({ key: activeKey, data: null, error: e?.message || 'Failed to load' });
      });
    return () => ctrl.abort();
  }, [activeKey, sn, range, bucket, fieldKey, date]);

  const resp = snap.key === activeKey ? snap.data : null;
  const error = snap.key === activeKey ? snap.error : null;
  const loading = !!activeKey && snap.key !== activeKey;

  const rows = useMemo(() => resp?.rows ?? [], [resp]);
  const unit = resp?.unit || device.units?.[fieldKey] || 'kWh';

  // Summary stats for the KPI strip. Anomalous buckets (kWh vs power mismatch)
  // are excluded from the "clean" total so a meter glitch doesn't distort it.
  const stats = useMemo(() => {
    const u = rows.map((r) => r.usage ?? 0);
    const count = u.length;
    const total = u.reduce((a, b) => a + b, 0);
    const peak = count ? Math.max(...u) : 0;
    const peakRow = rows.find((r) => (r.usage ?? 0) === peak);
    const anomalyCount = rows.filter((r) => r.anomaly).length;
    const cleanTotal = rows.filter((r) => !r.anomaly).reduce((s, r) => s + (r.usage ?? 0), 0);
    const cleanRows = rows.filter((r) => !r.anomaly);
    const on = rows.reduce((a, r) => a + (r.onpeak ?? 0), 0);
    const off = rows.reduce((a, r) => a + (r.offpeak ?? 0), 0);
    return {
      onPeak: on,
      offPeak: off,
      // Share of classified energy, so an unclassified sliver can't skew it.
      onPeakPct: on + off > 0 ? (on / (on + off)) * 100 : null,
      count,
      total,
      avg: count ? total / count : 0,
      peak,
      peakLabel: peakRow?.label ?? '—',
      anomalyCount,
      cleanTotal,
      hasPower: rows.some((r) => r.avgKw != null),
      cleanAvg: cleanRows.length ? cleanTotal / cleanRows.length : 0,
    };
  }, [rows]);

  const switchBucket = (b: 'hour' | 'day' | 'month') => {
    setDate(''); // leaving single-day mode
    setBucket(b);
    setRange(b === 'hour' ? '2d' : b === 'month' ? '365d' : '7d');
  };
  const dateLabel = date
    ? new Date(date + 'T00:00:00').toLocaleDateString(th ? 'th-TH' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  // Chronological (oldest → newest). The table reads in the same order as the
  // chart, so bar i and row i are the same bucket and the two views can be
  // linked by index.
  const chartRows = useMemo(() => [...rows].reverse(), [rows]);

  // Hovering a bar highlights its row and pulls it into view. Scrolling the
  // container by hand (rather than scrollIntoView) keeps the page itself still.
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  useEffect(() => {
    if (hoverIdx == null) return;
    const c = tableScrollRef.current;
    const el = rowRefs.current[hoverIdx];
    if (!c || !el) return;
    // The table is uncapped, so it only scrolls itself when something (a narrow
    // viewport) actually constrains it. Never scroll the page from a hover.
    if (c.scrollHeight <= c.clientHeight) return;
    const cr = c.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    // Header is sticky, so leave a row's worth of room above.
    const pad = 34;
    if (er.top < cr.top + pad) c.scrollTop += er.top - cr.top - pad;
    else if (er.bottom > cr.bottom) c.scrollTop += er.bottom - cr.bottom;
  }, [hoverIdx]);

  // TOU colouring is on only when the API actually classified something — a
  // meter with no split still renders the original single-colour bars.
  const touOn = !!resp?.meta?.peakConfigured && rows.some((r) => r.onpeak != null);
  const touRule = resp?.meta?.tou;

  const chartSvgRef = useRef<SVGSVGElement>(null);
  const bucketEN = displayBucket === 'hour' ? 'Hourly' : displayBucket === 'month' ? 'Monthly' : 'Daily';
  const baseName = `energy-${device.sn}-${date ? date : `${bucket}-${range}`}`;

  // ── Export: Excel (data table) · PDF (chart + table report) · PNG (chart image) ──
  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    const header = [
      'Time', 'Start', 'End',
      ...(stats.hasPower ? ['Avg kW'] : []),
      `Usage (${unit})`,
      ...(touOn ? [`On-Peak (${unit})`, `Off-Peak (${unit})`] : []),
      'Status',
    ];
    // Same order as the on-screen table and the chart: oldest → newest.
    const body = chartRows.map((r) => [
      r.label, r.start ?? '', r.end ?? '',
      ...(stats.hasPower ? [r.avgKw ?? ''] : []),
      r.usage ?? '',
      ...(touOn ? [r.onpeak ?? '', r.offpeak ?? ''] : []),
      r.anomaly ? 'Anomaly' : 'OK',
    ]);
    const total = [
      'Total', '', '',
      ...(stats.hasPower ? [''] : []),
      Number(stats.total.toFixed(2)),
      ...(touOn ? [Number(stats.onPeak.toFixed(2)), Number(stats.offPeak.toFixed(2))] : []),
      '',
    ];
    const aoa = [
      ['Energy Usage', device.name],
      ['Meter', fieldLabel, 'Period', `${bucketEN} · ${range}`],
      ...(touOn && touRule ? [['TOU', `On-Peak ${touRule.onPeak}`, `Off-Peak ${touRule.offPeak}`]] : []),
      [],
      header, ...body, [], total,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = header.map((h, i) => ({ wch: i === 0 ? 18 : Math.max(10, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Energy');
    XLSX.writeFile(wb, `${baseName}.xlsx`);
  };

  const exportPNG = async () => {
    if (!chartSvgRef.current) return;
    setHoverIdx(null);
    const { svgToPngBlob, downloadBlob, cssVar } = await import('@/lib/chart-export');
    const blob = await svgToPngBlob(chartSvgRef.current, { scale: 2, background: cssVar('--bg-surface', '#ffffff') });
    downloadBlob(blob, `${baseName}.png`);
  };

  const exportPDF = async () => {
    setHoverIdx(null);
    const [{ jsPDF }, exp] = await Promise.all([import('jspdf'), import('@/lib/chart-export')]);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 40;
    let y = 46;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20);
    doc.text('Energy Usage Report', M, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120);
    y += 16;
    doc.text(`${device.sn}  ·  ${fieldLabel}  ·  ${date ? date + ' (hourly)' : `${bucketEN} ${range}`}  ·  generated ${new Date().toLocaleString('en-GB')}`, M, y);
    // chart image
    if (chartSvgRef.current) {
      try {
        const blob = await exp.svgToPngBlob(chartSvgRef.current, { scale: 2, background: '#ffffff' });
        const dataUrl = await exp.blobToDataUrl(blob);
        const imgW = W - 2 * M;
        const imgH = imgW * (236 / 1000);
        y += 12;
        doc.addImage(dataUrl, 'PNG', M, y, imgW, imgH);
        y += imgH + 18;
      } catch { /* skip image on failure */ }
    }
    // summary
    doc.setTextColor(30); doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.text(
      `Total ${num(stats.total)} ${unit}   ·   Avg ${num(stats.avg)}   ·   Peak ${num(stats.peak)} (${stats.peakLabel})   ·   ${stats.count} readings${
        touOn ? `   ·   On-Peak ${num(stats.onPeak)} / Off-Peak ${num(stats.offPeak)} ${unit}` : ''
      }${stats.anomalyCount ? `   ·   ${stats.anomalyCount} anomaly` : ''}`,
      M, y,
    );
    y += 18;
    if (touOn && touRule) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(120);
      doc.text(`TOU  ·  On-Peak ${touRule.onPeak}  ·  Off-Peak ${touRule.offPeak}`, M, y);
      y += 16;
      doc.setTextColor(30);
    }
    // table (English labels; paginates)
    // Column widths are hand-fitted to the 762pt of landscape A4 inside the
    // margins; the TOU pair only fits once the others give some back.
    const cols = [
      { h: 'Time', w: touOn ? 118 : stats.hasPower ? 150 : 170, a: 'left' as const },
      { h: 'Start', w: touOn ? (stats.hasPower ? 98 : 110) : stats.hasPower ? 120 : 140, a: 'right' as const },
      { h: 'End', w: touOn ? (stats.hasPower ? 98 : 110) : stats.hasPower ? 120 : 140, a: 'right' as const },
      ...(stats.hasPower ? [{ h: 'Avg kW', w: touOn ? 70 : 90, a: 'right' as const }] : []),
      ...(touOn
        ? [
            { h: `On-Peak (${unit})`, w: stats.hasPower ? 88 : 96, a: 'right' as const },
            { h: `Off-Peak (${unit})`, w: stats.hasPower ? 88 : 96, a: 'right' as const },
          ]
        : []),
      { h: `Usage (${unit})`, w: touOn ? (stats.hasPower ? 100 : 110) : stats.hasPower ? 120 : 140, a: 'right' as const },
      { h: 'Status', w: touOn ? 70 : stats.hasPower ? 80 : 90, a: 'left' as const },
    ];
    const xs: number[] = []; { let cx = M; for (const c of cols) { xs.push(cx); cx += c.w; } }
    const rowH = 16;
    const drawHead = () => {
      doc.setFillColor(245, 246, 248); doc.rect(M, y - 11, cols.reduce((a, c) => a + c.w, 0), rowH, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(90);
      cols.forEach((c, i) => doc.text(c.h, c.a === 'right' ? xs[i] + c.w - 6 : xs[i] + 6, y, { align: c.a }));
      y += rowH;
    };
    drawHead();
    doc.setFont('helvetica', 'normal');
    for (const r of chartRows) {
      if (y > H - M) { doc.addPage(); y = 46; drawHead(); doc.setFont('helvetica', 'normal'); }
      doc.setTextColor(r.anomaly ? 200 : 40, r.anomaly ? 40 : 40, 40);
      const cells = [
        r.label, num(r.start), num(r.end),
        ...(stats.hasPower ? [num(r.avgKw, 1)] : []),
        ...(touOn ? [r.onpeak ? num(r.onpeak) : '—', r.offpeak ? num(r.offpeak) : '—'] : []),
        num(r.usage),
        r.anomaly ? 'Anomaly' : 'OK',
      ];
      doc.setFontSize(8.5);
      cells.forEach((val, i) => doc.text(String(val), cols[i].a === 'right' ? xs[i] + cols[i].w - 6 : xs[i] + 6, y, { align: cols[i].a }));
      y += rowH;
    }
    doc.save(`${baseName}.pdf`);
  };

  // ---- shared styles ----
  const segWrap: React.CSSProperties = {
    display: 'inline-flex',
    padding: 3,
    gap: 2,
    borderRadius: 999,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--glass-border)',
  };
  const seg = (active: boolean): React.CSSProperties => ({
    padding: '5px 14px',
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: 'none',
    transition: 'all .18s',
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? '#fff' : 'var(--text-muted)',
    boxShadow: active ? '0 2px 8px color-mix(in srgb, var(--accent) 40%, transparent)' : 'none',
  });
  const chip = (active: boolean): React.CSSProperties => ({
    padding: '5px 12px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all .15s',
    border: `1px solid ${active ? 'color-mix(in srgb, var(--accent) 55%, transparent)' : 'var(--glass-border)'}`,
    background: active ? 'color-mix(in srgb, var(--accent) 14%, transparent)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text-muted)',
  });
  const exportBtn = (color: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 13px',
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    color,
    border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`,
    background: `color-mix(in srgb, ${color} 12%, transparent)`,
    transition: 'all .15s',
  });
  const th_: React.CSSProperties = {
    textAlign: 'right',
    padding: '11px 18px',
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap',
    // Pins the header while the table scrolls itself, which now only happens
    // when a narrow viewport constrains it — the uncapped table otherwise
    // scrolls with the page and the header rides along.
    position: 'sticky',
    top: 0,
    background: 'var(--bg-elevated)',
    backdropFilter: 'blur(6px)',
    zIndex: 1,
  };
  const thLeft: React.CSSProperties = { ...th_, textAlign: 'left' };
  const td: React.CSSProperties = {
    textAlign: 'right',
    padding: '11px 18px',
    fontSize: 12.5,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  };
  const tdLeft: React.CSSProperties = { ...td, textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600 };
  // TOU columns: the header carries the same swatch as the chart legend, and a
  // period's own figure is tinted so a row's balance reads without the header.
  const touHead: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5 };
  const touDot = (c: string): React.CSSProperties => ({ width: 7, height: 7, borderRadius: 2, background: c, display: 'inline-block' });
  const touCell = (c: string, v: number | null | undefined): React.CSSProperties => ({
    ...td,
    color: v ? c : 'var(--text-muted)',
    fontWeight: v ? 700 : 400,
    opacity: v ? 1 : 0.5,
  });

  // KPI card
  const kpi = (label: string, value: string, sub: string, accent: string) => (
    <div
      style={{
        flex: '1 1 150px',
        minWidth: 150,
        padding: '14px 16px',
        borderRadius: 14,
        border: '1px solid var(--glass-border)',
        background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 10%, var(--bg-surface)), var(--bg-surface))`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent, opacity: 0.9 }} />
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, letterSpacing: '-0.5px' }}>
        {value}
        <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 5, color: 'var(--text-muted)' }}>{unit}</span>
      </div>
      <div style={{ marginTop: 5, fontSize: 10.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {sub}
      </div>
    </div>
  );

  const hasRows = rows.length > 0;

  return (
    <div
      style={{
        marginTop: 16,
        borderRadius: 18,
        border: '1px solid var(--glass-border)',
        background: 'var(--bg-surface)',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(0,0,0,0.12)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 14,
          padding: '16px 18px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'linear-gradient(180deg, color-mix(in srgb, var(--text-muted) 3%, transparent), transparent)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              background: `linear-gradient(135deg, ${GOLD}, #EF4444)`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              boxShadow: `0 4px 14px color-mix(in srgb, ${GOLD} 45%, transparent)`,
              flexShrink: 0,
            }}
          >
            ⚡
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.3px' }}>
              {th ? 'สรุปการใช้พลังงาน' : 'Energy Usage'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {device.name} · {fieldLabel}
            </span>
          </span>
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {energyFields.length > 1 && (
            <select
              value={fieldKey}
              onChange={(e) => setPickedKey(e.target.value)}
              style={{
                padding: '7px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit',
                border: '1px solid var(--glass-border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                cursor: 'pointer', outline: 'none',
              }}
            >
              {energyFields.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          )}
          {/* Period segmented control */}
          <div style={segWrap}>
            <button style={seg(bucket === 'hour')} onClick={() => switchBucket('hour')}>
              {th ? 'รายชั่วโมง' : 'Hourly'}
            </button>
            <button style={seg(bucket === 'day')} onClick={() => switchBucket('day')}>
              {th ? 'รายวัน' : 'Daily'}
            </button>
            <button style={seg(bucket === 'month')} onClick={() => switchBucket('month')}>
              {th ? 'รายเดือน' : 'Monthly'}
            </button>
          </div>
          {/* Range chips (hidden while a single day is selected) */}
          {!date && (
            <div style={{ display: 'flex', gap: 6 }}>
              {RANGE_OPTIONS[bucket].map((o) => (
                <button key={o.value} style={chip(range === o.value)} onClick={() => setRange(o.value)}>
                  {th ? o.th : o.en}
                </button>
              ))}
            </div>
          )}

          {/* Single-day picker: choose a day → hourly breakdown for that day */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="date"
              max={bkkToday()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              title={th ? 'เลือกวันเพื่อดูรายชั่วโมง' : 'Pick a day for the hourly view'}
              style={{
                padding: '5px 12px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: 'inherit',
                border: `1px solid ${date ? 'color-mix(in srgb, var(--accent) 55%, transparent)' : 'var(--glass-border)'}`,
                background: date ? 'color-mix(in srgb, var(--accent) 14%, transparent)' : 'transparent',
                color: date ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer',
                outline: 'none',
                colorScheme: 'light dark',
              }}
            />
            {date && (
              <button onClick={() => setDate('')} title={th ? 'ล้างวัน' : 'Clear day'} style={{ ...chip(false), padding: '5px 10px' }}>
                ✕
              </button>
            )}
          </div>

          {/* Export buttons */}
          {hasRows && !loading && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={exportBtn('#10B981')} onClick={exportExcel} title="Export Excel (.xlsx)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>
                Excel
              </button>
              <button style={exportBtn('#EF4444')} onClick={exportPDF} title="Export PDF report">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
                PDF
              </button>
              <button style={exportBtn('#3B82F6')} onClick={exportPNG} title="Export chart PNG">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="1.6" /><path d="m21 15-5-5L5 21" /></svg>
                PNG
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KPI strip */}
      {hasRows && !loading && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '16px 18px 4px' }}>
          {kpi(th ? 'ใช้รวม' : 'Total Usage', compact(stats.total), th ? `${stats.count} ช่วง` : `${stats.count} periods`, GOLD)}
          {kpi(th ? 'เฉลี่ย/ช่วง' : 'Avg / period', compact(stats.avg), displayBucket === 'hour' ? (th ? 'ต่อชั่วโมง' : 'per hour') : displayBucket === 'month' ? (th ? 'ต่อเดือน' : 'per month') : (th ? 'ต่อวัน' : 'per day'), '#0EA5E9')}
          {kpi(th ? 'สูงสุด' : 'Peak', compact(stats.peak), stats.peakLabel, '#EF4444')}
          {kpi(th ? 'จำนวนช่วง' : 'Readings', String(stats.count), th ? (displayBucket === 'hour' ? 'ชั่วโมง' : displayBucket === 'month' ? 'เดือน' : 'วัน') : displayBucket, '#8B5CF6')}
        </div>
      )}

      {/* Energy consumption bar chart (usage per bucket, chronological) */}
      {hasRows && !loading && (
        <div style={{ padding: '12px 18px 2px' }}>
          <div
            style={{
              borderRadius: 16,
              border: '1px solid var(--glass-border)',
              background: 'linear-gradient(180deg, color-mix(in srgb, var(--text-muted) 3.5%, var(--bg-surface)), var(--bg-surface))',
              padding: '14px 16px 8px',
            }}
          >
            {/* chart header + legend */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '0 2px 12px' }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.2px' }}>
                {th ? 'กราฟการใช้พลังงาน' : 'Energy Consumption'}
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 8 }}>
                  {unit} ·{' '}
                  {date
                    ? `${th ? 'รายชั่วโมง' : 'Hourly'} · ${dateLabel}`
                    : th
                      ? bucket === 'hour' ? 'รายชั่วโมง' : bucket === 'day' ? 'รายวัน' : 'รายเดือน'
                      : bucket}
                </span>
                {!date && displayBucket === 'day' && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)', marginLeft: 8 }}>
                    · {th ? 'คลิกแท่งเพื่อดูรายชั่วโมง' : 'click a bar for hourly'}
                  </span>
                )}
              </span>
              <span style={{ display: 'flex', gap: 14, fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 600, flexWrap: 'wrap' }}>
                {touOn ? (
                  <>
                    <span
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      title={th ? touRule?.onPeakTh : touRule?.onPeak}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: `linear-gradient(180deg, ${TOU_ON}, ${GOLD})` }} />
                      On-Peak
                      {stats.onPeakPct != null && (
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>{stats.onPeakPct.toFixed(0)}%</span>
                      )}
                    </span>
                    <span
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      title={th ? touRule?.offPeakTh : touRule?.offPeak}
                    >
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: `linear-gradient(180deg, ${TOU_OFF}, #A5B4FC)` }} />
                      Off-Peak
                      {stats.onPeakPct != null && (
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>{(100 - stats.onPeakPct).toFixed(0)}%</span>
                      )}
                    </span>
                  </>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: `linear-gradient(180deg, #EF4444, ${GOLD})` }} />
                    {th ? 'การใช้ไฟ' : 'Usage'}
                  </span>
                )}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 14, height: 0, borderTop: '2px dashed #0EA5E9' }} />
                  {th ? 'เฉลี่ย' : 'Avg'}
                </span>
                {stats.anomalyCount > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: '#EF4444' }} />
                    {th ? 'ผิดปกติ' : 'Anomaly'}
                  </span>
                )}
              </span>
            </div>
            {(() => {
              const vw = 1000, vh = 236, mL = 50, mR = 16, mT = 22, mB = 42;
              const pw = vw - mL - mR, ph = vh - mT - mB;
              const baseY = mT + ph;
              const n = chartRows.length;
              const usages = chartRows.map((r) => r.usage ?? 0);
              const peak = Math.max(1, ...usages);
              const peakIdx = usages.indexOf(Math.max(...usages));
              const avg = n ? usages.reduce((a, b) => a + b, 0) / n : 0;
              const niceMax = (() => {
                const p = Math.pow(10, Math.floor(Math.log10(peak)));
                const s = peak / p;
                const m = s <= 1 ? 1 : s <= 1.5 ? 1.5 : s <= 2 ? 2 : s <= 3 ? 3 : s <= 5 ? 5 : 10;
                return m * p;
              })();
              const bw = pw / Math.max(n, 1);
              const barW = Math.max(1.5, Math.min(bw * 0.68, 40));
              const rad = Math.min(4, barW / 2);
              const yAt = (v: number) => mT + (1 - v / niceMax) * ph;
              const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => niceMax * f);
              const showValues = n <= 16;
              const step = Math.max(1, Math.ceil(n / (n > 40 ? 8 : 10)));
              // rounded-top bar path (flat bottom)
              const barPath = (x: number, y: number, w: number, h: number) => segPath(x, y, w, baseY, h);
              // Same shape but with a caller-chosen flat bottom, so a stacked
              // bar's upper segment keeps the bar's rounded top while its lower
              // segment sits flush on the one below.
              function segPath(x: number, y: number, w: number, bottom: number, h = bottom - y) {
                const r = Math.min(rad, w / 2, Math.max(0, h));
                return `M${x},${bottom} L${x},${(y + r).toFixed(1)} Q${x},${y} ${(x + r).toFixed(1)},${y} L${(x + w - r).toFixed(1)},${y} Q${(x + w).toFixed(1)},${y} ${(x + w).toFixed(1)},${(y + r).toFixed(1)} L${(x + w).toFixed(1)},${bottom} Z`;
              }
              const hv = hoverIdx != null && hoverIdx >= 0 && hoverIdx < n ? chartRows[hoverIdx] : null;
              return (
                <svg
                  ref={chartSvgRef}
                  viewBox={`0 0 ${vw} ${vh}`}
                  width="100%"
                  height="auto"
                  style={{ display: 'block', overflow: 'visible' }}
                  preserveAspectRatio="xMidYMid meet"
                  onMouseLeave={() => setHoverIdx(null)}
                >
                  <defs>
                    <linearGradient id="me-bar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F97316" />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0.72} />
                    </linearGradient>
                    <linearGradient id="me-bar-hi" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" />
                      <stop offset="100%" stopColor="#F59E0B" />
                    </linearGradient>
                    <linearGradient id="me-bar-off" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={TOU_OFF} />
                      <stop offset="100%" stopColor="#A5B4FC" stopOpacity={0.8} />
                    </linearGradient>
                    <linearGradient id="me-bar-bad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>

                  {/* gridlines + y labels */}
                  {ticks.map((t, i) => (
                    <g key={i}>
                      <line x1={mL} y1={yAt(t)} x2={vw - mR} y2={yAt(t)} stroke="var(--border-subtle)" strokeWidth={1} strokeDasharray={i === 0 ? undefined : '3 5'} strokeOpacity={i === 0 ? 1 : 0.7} />
                      <text x={mL - 8} y={yAt(t) + 3.5} textAnchor="end" fontSize={9.5} fill="var(--text-muted)" style={{ fontVariantNumeric: 'tabular-nums' }}>{compact(t)}</text>
                    </g>
                  ))}

                  {/* bars — split into on-peak / off-peak segments when the TOU
                      classification is available. An hour bucket lands wholly in
                      one period, so it renders as a single coloured bar. */}
                  {chartRows.map((r, i) => {
                    const v = r.usage ?? 0;
                    const x = mL + i * bw + (bw - barW) / 2;
                    const y = v > 0 ? Math.min(yAt(v), baseY - 2) : baseY;
                    const h = Math.max(0, baseY - y);
                    const bad = !!r.anomaly;
                    const active = hoverIdx === i;
                    const dim = { transition: 'opacity .15s', opacity: hoverIdx == null || active ? 1 : 0.55 };
                    // The peak bar used to be filled red; with TOU colours that
                    // would read as a period, so it's an outline now.
                    const peakRing =
                      touOn && i === peakIdx && !bad ? (
                        <path key={`pk${i}`} d={barPath(x, y, barW, h)} fill="none" stroke="#EF4444" strokeWidth={1.4} style={dim} />
                      ) : null;

                    if (bad || !touOn || v <= 0) {
                      const fill = bad ? 'url(#me-bar-bad)' : active || i === peakIdx ? 'url(#me-bar-hi)' : 'url(#me-bar)';
                      return <path key={i} d={barPath(x, y, barW, h)} fill={fill} style={dim} />;
                    }

                    const on = r.onpeak ?? 0;
                    const off = r.offpeak ?? 0;
                    const classified = on + off;
                    if (classified <= 0) {
                      return <path key={i} d={barPath(x, y, barW, h)} fill="url(#me-bar)" style={dim} />;
                    }
                    // Segment heights come from the on/off ratio applied to the
                    // bar's own usage, so the bar keeps its true total height
                    // even when the hourly deltas sum a hair under it.
                    const onH = h * (on / classified);
                    const splitY = baseY - onH;
                    if (off <= 0 || on <= 0) {
                      return (
                        <g key={i}>
                          <path d={barPath(x, y, barW, h)} fill={off <= 0 ? 'url(#me-bar)' : 'url(#me-bar-off)'} style={dim} />
                          {peakRing}
                        </g>
                      );
                    }
                    return (
                      <g key={i}>
                        {/* off-peak on top (keeps the rounded cap), on-peak below */}
                        <path d={segPath(x, y, barW, splitY)} fill="url(#me-bar-off)" style={dim} />
                        <rect x={x} y={splitY} width={barW} height={onH} fill="url(#me-bar)" style={dim} />
                        {peakRing}
                      </g>
                    );
                  })}

                  {/* value labels (only when few bars) */}
                  {showValues &&
                    chartRows.map((r, i) => {
                      const v = r.usage ?? 0;
                      if (v <= 0) return null;
                      return (
                        <text key={`v${i}`} x={mL + i * bw + bw / 2} y={yAt(v) - 6} textAnchor="middle" fontSize={9.5} fontWeight={700} fill="var(--text-secondary)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {compact(v)}
                        </text>
                      );
                    })}

                  {/* average reference line */}
                  {avg > 0 && (
                    <g>
                      <line x1={mL} y1={yAt(avg)} x2={vw - mR} y2={yAt(avg)} stroke="#0EA5E9" strokeWidth={1.4} strokeDasharray="5 4" strokeOpacity={0.9} />
                      <g transform={`translate(${vw - mR - 2}, ${yAt(avg)})`}>
                        <rect x={-58} y={-9} width={58} height={16} rx={5} fill="color-mix(in srgb, #0EA5E9 16%, var(--bg-surface))" stroke="#0EA5E9" strokeOpacity={0.5} />
                        <text x={-29} y={3} textAnchor="middle" fontSize={9} fontWeight={700} fill="#0EA5E9">Ø {compact(avg)}</text>
                      </g>
                    </g>
                  )}

                  {/* x labels (thinned, anchored at ends) */}
                  {chartRows.map((r, i) =>
                    i % step === 0 || i === n - 1 ? (
                      <text key={`x${i}`} x={mL + i * bw + bw / 2} y={vh - 14} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize={9} fill="var(--text-muted)">
                        {r.label}
                      </text>
                    ) : null
                  )}

                  {/* hover hit-areas (full-height columns) — click a daily bar to drill into that day's hours */}
                  {chartRows.map((r, i) => {
                    const drill = !date && displayBucket === 'day' && !!r.bucketKey;
                    return (
                      <rect
                        key={`h${i}`}
                        x={mL + i * bw}
                        y={mT}
                        width={bw}
                        height={ph}
                        fill="transparent"
                        onMouseEnter={() => setHoverIdx(i)}
                        onClick={drill ? () => setDate((r.bucketKey as string).slice(0, 10)) : undefined}
                        style={{ cursor: drill ? 'pointer' : 'default' }}
                      >
                        {drill && <title>{th ? `คลิกดูรายชั่วโมงของ ${r.label}` : `Click for hourly of ${r.label}`}</title>}
                      </rect>
                    );
                  })}

                  {/* hover marker + tooltip */}
                  {hv && (() => {
                    const v = hv.usage ?? 0;
                    const cx = mL + (hoverIdx as number) * bw + bw / 2;
                    const topY = v > 0 ? yAt(v) : baseY;
                    const bad = !!hv.anomaly;
                    const on = hv.onpeak ?? 0;
                    const off = hv.offpeak ?? 0;
                    // Only worth a breakdown line when the bucket spans both.
                    const showSplit = touOn && !bad && on > 0 && off > 0;
                    const tw = showSplit ? 150 : 128, tht = showSplit ? 57 : 40;
                    const tx = Math.max(mL, Math.min(cx - tw / 2, vw - mR - tw));
                    const ty = Math.max(2, topY - tht - 10);
                    return (
                      <g pointerEvents="none">
                        <line x1={cx} y1={topY} x2={cx} y2={baseY} stroke="var(--text-muted)" strokeWidth={1} strokeDasharray="2 3" strokeOpacity={0.5} />
                        <circle cx={cx} cy={topY} r={3} fill={bad ? '#EF4444' : GOLD} stroke="var(--bg-surface)" strokeWidth={1.5} />
                        <rect x={tx} y={ty} width={tw} height={tht} rx={9} fill="var(--bg-elevated)" stroke="var(--glass-border)" style={{ filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.28))' }} />
                        <text x={tx + 10} y={ty + 16} fontSize={9.5} fill="var(--text-muted)" fontWeight={600}>{hv.label}</text>
                        <text x={tx + 10} y={ty + 31} fontSize={13} fontWeight={800} fill={bad ? '#EF4444' : 'var(--text-primary)'} style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {num(v)} <tspan fontSize={9.5} fontWeight={600} fill="var(--text-muted)">{unit}</tspan>
                          {bad && <tspan fontSize={11} fill="#EF4444"> ⚠︎</tspan>}
                        </text>
                        {showSplit && (
                          <>
                            <rect x={tx + 10} y={ty + 38} width={7} height={7} rx={2} fill={TOU_ON} />
                            <text x={tx + 21} y={ty + 44.5} fontSize={9} fill="var(--text-muted)" fontWeight={600}>On</text>
                            <text x={tx + 68} y={ty + 44.5} textAnchor="end" fontSize={9} fontWeight={700} fill="var(--text-secondary)" style={{ fontVariantNumeric: 'tabular-nums' }}>{num(on, 1)}</text>
                            <rect x={tx + 78} y={ty + 38} width={7} height={7} rx={2} fill={TOU_OFF} />
                            <text x={tx + 89} y={ty + 44.5} fontSize={9} fill="var(--text-muted)" fontWeight={600}>Off</text>
                            <text x={tx + tw - 10} y={ty + 44.5} textAnchor="end" fontSize={9} fontWeight={700} fill="var(--text-secondary)" style={{ fontVariantNumeric: 'tabular-nums' }}>{num(off, 1)}</text>
                          </>
                        )}
                      </g>
                    );
                  })()}
                </svg>
              );
            })()}
          </div>
        </div>
      )}

      {/* Anomaly banner — kWh not consistent with measured power */}
      {hasRows && !loading && stats.anomalyCount > 0 && (
        <div
          style={{
            margin: '4px 18px 0',
            padding: '10px 14px',
            borderRadius: 12,
            border: '1px solid color-mix(in srgb, #EF4444 40%, transparent)',
            background: 'color-mix(in srgb, #EF4444 8%, transparent)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            {th
              ? `พบ ${stats.anomalyCount} ช่วงที่ค่า kWh ไม่สอดคล้องกับกำลังไฟ (kW) — น่าจะเป็นความผิดพลาดของมิเตอร์ (ค่าสะสมกระโดด) ไม่ใช่การใช้ไฟจริง · `
              : `${stats.anomalyCount} period(s) where kWh doesn't match measured power — likely a meter glitch (cumulative jump), not real usage · `}
            <b style={{ color: 'var(--text-primary)' }}>
              {th ? 'ใช้จริงโดยประมาณ ' : 'Est. real total '}
              {compact(stats.cleanTotal)} {unit}
            </b>
          </span>
        </div>
      )}

      {/* Table */}
      <div style={{ padding: hasRows && !loading ? '12px 12px 12px' : 0 }}>
        {/* No height cap: every bucket is listed and the page does the
            scrolling. Horizontal scroll stays, so the TOU columns don't push
            the layout sideways on a narrow screen. */}
        <div
          ref={tableScrollRef}
          style={{
            overflowX: 'auto',
            borderRadius: hasRows && !loading ? 12 : 0,
            border: hasRows && !loading ? '1px solid var(--border-subtle)' : 'none',
          }}
        >
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {th ? 'กำลังโหลด…' : 'Loading…'}
            </div>
          ) : error ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--red)', fontSize: 13 }}>{error}</div>
          ) : !hasRows ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {resp?.meta?.reason === 'field_key_mismatch' || resp?.meta?.reason === 'no_field_config'
                ? th
                  ? 'อุปกรณ์นี้ไม่มีมิเตอร์ค่าพลังงานสะสม (kWh)'
                  : 'No cumulative kWh meter on this device'
                : th
                  ? 'ไม่มีข้อมูลพลังงานในช่วงที่เลือก'
                  : 'No energy data for the selected range'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={thLeft}>{th ? 'เวลา' : 'Time'}</th>
                  <th style={th_}>{th ? 'ค่าเริ่ม' : 'Start'}</th>
                  <th style={th_}>{th ? 'ค่าสิ้นสุด' : 'End'}</th>
                  {stats.hasPower && <th style={th_}>{th ? 'กำลังเฉลี่ย' : 'Avg kW'}</th>}
                  {touOn && (
                    <>
                      <th style={th_} title={th ? touRule?.onPeakTh : touRule?.onPeak}>
                        <span style={touHead}><span style={touDot(TOU_ON)} />On-Peak</span>
                      </th>
                      <th style={th_} title={th ? touRule?.offPeakTh : touRule?.offPeak}>
                        <span style={touHead}><span style={touDot(TOU_OFF)} />Off-Peak</span>
                      </th>
                    </>
                  )}
                  <th style={{ ...th_, width: touOn ? '26%' : '34%' }}>{th ? 'ใช้ไป' : 'Usage'}</th>
                </tr>
              </thead>
              <tbody>
                {chartRows.map((r, i) => {
                  const pct = stats.peak > 0 ? Math.max(2, ((r.usage ?? 0) / stats.peak) * 100) : 0;
                  const bad = !!r.anomaly;
                  const linked = hoverIdx === i;
                  // The usage bar carries the same split as the stacked chart
                  // bar: on-peak first, off-peak after, hard stop between.
                  const on = r.onpeak ?? 0;
                  const off = r.offpeak ?? 0;
                  const barFill = bad
                    ? '#EF4444'
                    : !touOn || on + off <= 0
                      ? `linear-gradient(90deg, ${GOLD}, #EF4444)`
                      : off <= 0
                        ? `linear-gradient(90deg, ${TOU_ON}, ${GOLD})`
                        : on <= 0
                          ? `linear-gradient(90deg, ${TOU_OFF}, #A5B4FC)`
                          : `linear-gradient(90deg, ${TOU_ON} 0 ${(on / (on + off)) * 100}%, ${TOU_OFF} ${(on / (on + off)) * 100}% 100%)`;
                  const tip = bad
                    ? th
                      ? `ผิดปกติ: ใช้ ${num(r.usage)} ${unit} แต่กำลังไฟเฉลี่ย ${num(r.avgKw, 1)} kW คาดว่าควรเป็น ~${num(r.expectedKwh)} ${unit}`
                      : `Anomaly: ${num(r.usage)} ${unit} but avg power ${num(r.avgKw, 1)} kW → expected ~${num(r.expectedKwh)} ${unit}`
                    : undefined;
                  return (
                    <tr
                      key={i}
                      ref={(el) => { rowRefs.current[i] = el; }}
                      title={tip}
                      style={{
                        borderTop: '1px solid var(--border-subtle)',
                        transition: 'background .15s, box-shadow .15s',
                        background: linked
                          ? 'var(--gold-dim)'
                          : bad
                            ? 'color-mix(in srgb, #EF4444 6%, transparent)'
                            : 'transparent',
                        // Inset instead of a border so the row doesn't shift.
                        boxShadow: linked ? `inset 3px 0 0 ${GOLD}` : undefined,
                      }}
                      onMouseEnter={(e) => { if (!linked) e.currentTarget.style.background = bad ? 'color-mix(in srgb, #EF4444 12%, transparent)' : 'var(--bg-elevated)'; }}
                      onMouseLeave={(e) => { if (!linked) e.currentTarget.style.background = bad ? 'color-mix(in srgb, #EF4444 6%, transparent)' : 'transparent'; }}
                    >
                      <td style={tdLeft}>{r.label}</td>
                      <td style={td}>{num(r.start)}</td>
                      <td style={td}>{num(r.end)}</td>
                      {stats.hasPower && <td style={td}>{num(r.avgKw, 1)}</td>}
                      {touOn && (
                        <>
                          {/* An hour bucket sits wholly in one period, so the
                              other side is a dash rather than a row of 0.00. */}
                          <td style={touCell(TOU_ON, r.onpeak)}>{r.onpeak ? num(r.onpeak) : '—'}</td>
                          <td style={touCell(TOU_OFF, r.offpeak)}>{r.offpeak ? num(r.offpeak) : '—'}</td>
                        </>
                      )}
                      <td style={{ ...td, padding: '9px 18px' }}>
                        {/* mini usage bar behind the number */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                          {bad && <span style={{ fontSize: 13 }} title={tip}>⚠️</span>}
                          <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'color-mix(in srgb, var(--text-muted) 12%, transparent)', overflow: 'hidden', maxWidth: 140 }}>
                            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: barFill }} />
                          </div>
                          <span style={{ minWidth: 64, textAlign: 'right', fontWeight: 800, color: bad ? '#EF4444' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                            {num(r.usage)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
